import * as Sentry from "@sentry/react"
import {
  createContext,
  useContext,
  type PropsWithChildren,
  useState,
  useEffect,
} from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { userApi } from "@/api/user.api.ts"
import { getStoredLocale } from "@/lib/utils.ts"
import i18n from "@/i18n"
import { identifyCustomer } from "@/lib/customer-io.ts"

export enum Gender {
  Man = 1,
  Woman = 2,
  PreferNotToSay = 3,
}

const genders = [
  { id: 1, name: "Man" },
  { id: 2, name: "Woman" },
  { id: 3, name: "Prefer not to say" },
]

const UserContext = createContext<any | null>(null)

export function UserProvider({ children }: PropsWithChildren) {
  const storedLocale = getStoredLocale()
  const [locale, setLocale] = useState(storedLocale)
  const userQuery = useQuery({
    queryKey: ["user/me"],
    queryFn: () => userApi.me(),
    staleTime: 60 * 60 * 1000,
    gcTime: 120 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })

  const profilePictureQuery = useQuery({
    queryKey: ["user/profilePicture", userQuery.data?.result?.user?.id],
    queryFn: () => userApi.getProfilePicture(userQuery.data?.result?.user?.id),
    staleTime: 60 * 60 * 1000,
    gcTime: 120 * 60 * 1000,
    enabled: !!userQuery.data?.result?.user?.id,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })

  useEffect(() => {
    if (userQuery.data?.result?.locale) {
      localStorage.setItem("egab_locale", userQuery.data?.result?.locale)
      setLocale(userQuery.data?.result?.locale)
      i18n.changeLanguage(userQuery.data?.result?.locale)
    }
    const currentUser = userQuery.data?.result?.user

    Sentry.setUser({
      id: currentUser?.publicId,
      email: currentUser?.emailAddress,
    })

    if (currentUser?.emailAddress) {
      identifyCustomer(currentUser?.publicId, {
        id: currentUser?.publicId,
        publicId: currentUser?.publicId,
        platformId: currentUser?.id,
        totalPitches: currentUser?.totalPitches,
        email: currentUser?.emailAddress,
        creationDate: currentUser?.creationDate,
        firstName: currentUser?.name,
        country: currentUser?.countryName,
        city: currentUser?.city,
        gender:
          genders.find((gender) => gender.id === currentUser?.gender)?.name ||
          "Prefer not to say",
        language: userQuery.data?.result?.locale,
        createdAt: currentUser?.creationDate,
        role: "journalist",
      })
    }
  }, [userQuery.data?.result])

  const changeLocale = useMutation({
    mutationFn: userApi.changeLocale,
    onSuccess: (_data, variables) => {
      localStorage.setItem("egab_locale", variables)
      window.location.reload()
    },
  })

  const onChangeLocale = (locale: string) => changeLocale.mutate(locale)

  return (
    <UserContext.Provider
      value={{
        user: userQuery.data?.result?.user,
        profilePicture: profilePictureQuery.data?.result?.profilePicture
          ? `data:image/png;base64,${profilePictureQuery.data.result.profilePicture}`
          : "",
        isLoading:
          userQuery.isLoading ||
          (userQuery.isSuccess && profilePictureQuery.isLoading),
        isError: userQuery.isError || profilePictureQuery.isError,
        locale: locale,
        refetchUser: () => {
          userQuery.refetch()
        },
        onChangeLocale: onChangeLocale,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)

  if (!context) {
    throw new Error("useUser must be used within UserProvider")
  }

  return context
}
